import { useEffect, useRef, useCallback, useMemo } from "react";
import { APIProvider, Map, Marker, useMap } from "@vis.gl/react-google-maps";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const UK_CENTER = { lat: 54.2, lng: -4.5 };

const DARK_STYLES = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#8a8a8a" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] },
];

const BASEMAP_CONFIG = {
  satellite: { mapTypeId: "hybrid", styles: null },
  dark: { mapTypeId: "roadmap", styles: DARK_STYLES },
  streets: { mapTypeId: "roadmap", styles: null },
};

const CLASS_EMPHASIS = { sub250: 0.75, a2: 1.0, heavy: 1.4 };

const zoneStyleFor = (feature, weightClass) => {
  const t = feature.getProperty("zone_type");
  const color = feature.getProperty("color");
  const emph = CLASS_EMPHASIS[weightClass] || 1;
  let fillOpacity = 0.2;
  let strokeWeight = 1.8;
  if (t === "FRZ" || t === "PROHIBITED") { fillOpacity = 0.24; strokeWeight = 2.3; }
  else if (t === "RESTRICTED") fillOpacity = 0.18;
  else if (t === "HIRTA" || t === "DANGER") fillOpacity = 0.13;
  else if (t === "COUNCIL") fillOpacity = 0.2;
  else fillOpacity = 0.16;
  return {
    fillColor: color,
    fillOpacity: Math.min(fillOpacity * emph, 0.5),
    strokeColor: color,
    strokeOpacity: 0.9,
    strokeWeight: weightClass === "heavy" ? strokeWeight + 0.5 : strokeWeight,
  };
};

const popupHtml = (f) => {
  const permit = f.getProperty("permit_url")
    ? `<a href="${f.getProperty("permit_url")}" target="_blank" rel="noreferrer" style="color:#1a73e8">Request council permit →</a>`
    : "";
  return `<div style="max-width:260px;font-family:sans-serif">
    <div style="font-weight:700;font-size:14px;margin-bottom:4px;color:#111">${f.getProperty("name")}</div>
    <div style="font-size:12px;color:${f.getProperty("color")};font-weight:700;letter-spacing:0.06em;margin-bottom:6px">${String(f.getProperty("restriction_level")).replaceAll("_", " ")} · ${f.getProperty("source_agency")}</div>
    <div style="font-size:13px;color:#444;line-height:1.45;max-height:110px;overflow:auto">${f.getProperty("description")}</div>
    ${permit}</div>`;
};

const spotIconUrl = "data:image/svg+xml," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26"><circle cx="13" cy="13" r="11" fill="#2ec4b6" stroke="white" stroke-width="2.5"/><path d="M13 6.5l1.9 4.1 4.5.5-3.3 3 .9 4.4L13 16.3l-4 2.2.9-4.4-3.3-3 4.5-.5z" fill="#0a0a0a"/></svg>`
);

const pinIconUrl = (noFly) => "data:image/svg+xml," + encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22"><circle cx="11" cy="11" r="8" fill="${noFly ? "#ff4d4d" : "#2ec4b6"}" stroke="white" stroke-width="3"/></svg>`
);

const MapContent = ({ zones, zonesKey, weightClass, flyTarget, basemap, mapRef, onSpotSelect, spots, selectedPoint, status }) => {
  const map = useMap();
  const infoRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google) return;
    const ov = new window.google.maps.OverlayView();
    ov.onAdd = () => {};
    ov.draw = () => {};
    ov.onRemove = () => {};
    ov.setMap(map);
    overlayRef.current = ov;
    mapRef.current = {
      toLatLng: (clientX, clientY) => {
        const proj = ov.getProjection();
        if (!proj) return null;
        const rect = map.getDiv().getBoundingClientRect();
        const pt = new window.google.maps.Point(clientX - rect.left, clientY - rect.top);
        const ll = proj.fromContainerPixelToLatLng(pt);
        return ll ? { lat: ll.lat(), lng: ll.lng() } : null;
      },
    };
    return () => ov.setMap(null);
  }, [map, mapRef]);

  useEffect(() => {
    if (!map || !zones || !window.google || !map.data) return;
    map.data.forEach((f) => map.data.remove(f));
    map.data.addGeoJson(zones);
    map.data.setStyle((f) => zoneStyleFor(f, weightClass));
    const listener = map.data.addListener("click", (e) => {
      if (!infoRef.current) infoRef.current = new window.google.maps.InfoWindow();
      infoRef.current.setContent(popupHtml(e.feature));
      infoRef.current.setPosition(e.latLng);
      infoRef.current.open(map);
    });
    return () => listener && listener.remove();
  }, [map, zones, zonesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (map && map.data) map.data.setStyle((f) => zoneStyleFor(f, weightClass));
  }, [map, weightClass]);

  useEffect(() => {
    if (!map || !map.setMapTypeId) return;
    const cfg = BASEMAP_CONFIG[basemap] || BASEMAP_CONFIG.satellite;
    map.setMapTypeId(cfg.mapTypeId);
    map.setOptions({ styles: cfg.styles });
  }, [map, basemap]);

  useEffect(() => {
    if (!map || !flyTarget || !map.panTo) return;
    map.panTo({ lat: flyTarget.lat, lng: flyTarget.lng });
    map.setZoom(flyTarget.zoom || 13);
  }, [map, flyTarget]);

  const makeSize = useCallback((w, h) => (window.google ? new window.google.maps.Size(w, h) : undefined), []);

  const spotMarkers = useMemo(
    () =>
      spots.map((s) => (
        <Marker
          key={s.name}
          position={{ lat: s.lat, lng: s.lng }}
          icon={{ url: spotIconUrl, scaledSize: makeSize(26, 26) }}
          title={`${s.name} — ${s.region}`}
          onClick={() => onSpotSelect && onSpotSelect(s)}
        />
      )),
    [spots, onSpotSelect, makeSize]
  );

  const pinMarker = useMemo(
    () =>
      selectedPoint ? (
        <Marker
          position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
          icon={{ url: pinIconUrl(status === "STRICT_NO_FLY"), scaledSize: makeSize(22, 22) }}
        />
      ) : null,
    [selectedPoint, status, makeSize]
  );

  return (
    <>
      {spotMarkers}
      {pinMarker}
    </>
  );
};

export const GoogleMapView = ({ zones, zonesKey, onMapClick, selectedPoint, flyTarget, status, weightClass, spots = [], onSpotSelect, basemap = "satellite", mapRef }) => {
  return (
    <div className="absolute inset-0 z-0" data-testid="map-container" aria-label="Interactive Drone Map">
      <APIProvider apiKey={KEY}>
        <Map
          defaultCenter={UK_CENTER}
          defaultZoom={6}
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={true}
          mapTypeId="hybrid"
          onClick={(e) => e.detail.latLng && onMapClick(e.detail.latLng.lat, e.detail.latLng.lng)}
          className="h-full w-full"
        >
          <MapContent {...{ zones, zonesKey, weightClass, flyTarget, basemap, mapRef, onSpotSelect, spots, selectedPoint, status }} />
        </Map>
      </APIProvider>
    </div>
  );
};
